import { useSnackbar } from '@app/hooks/useSnackbar';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { DocumentSkeleton } from '@components/block/OutputView/DocumentSkeleton';
import { CloseButton } from '@components/ui/CloseButton/CloseButton';
import { useDpoTaskDeleteFileMutation } from '@dieterApi/dpo/useDpoTaskDeleteFileMutation';
import { useDpoTaskDocumentPreviewQuery } from '@dieterApi/dpo/useDpoTaskDocumentPreviewQuery';
import { useDpoTaskSetApprovalMutation } from '@dieterApi/dpo/useDpoTaskSetApprovalMutation';
import { DpoState, useDpoTasksQuery } from '@dieterApi/dpo/useDpoTasksQuery';
import { useDpoTaskUploadFileMutation } from '@dieterApi/dpo/useDpoTaskUploadFileMutation';
import { UserRole } from '@dieterApi/user/useUserQuery';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Modal,
  Paper,
} from '@mui/material';
import cx from 'classnames';
import { MUIDataTableMeta } from 'mui-datatables';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DpoChat } from './DpoChat';
import { DpoDocumentViewer } from './DpoDocumentViewer';
import { UploadedDocumentDownloadBar } from './UploadedDocumentDownloadBar';
import { UploadedDocumentHeader } from './UploadedDocumentHeader';

export interface DocumentComparerProps {
  onClose: () => void;
  tableMeta?: MUIDataTableMeta | null;
  localQuestionnaireId?: string;
  documentNumber?: number;
}

export function DocumentComparer({ onClose, tableMeta, localQuestionnaireId, documentNumber }: DocumentComparerProps) {
  const { t } = useTranslation();
  const { user, activeCompany } = useUserWithCompany();
  const companyId = activeCompany?.id;

  // Query DPO tasks to get current hasFile status from cache
  const { data: dpoTasksData } = useDpoTasksQuery(companyId);

  // Find task by either taskId from tableMeta or questionnaireId
  const taskIdFromMeta = tableMeta?.rowData?.[0] as string | undefined;
  const currentTask = dpoTasksData?.getDpoTasks.find((task) =>
    taskIdFromMeta ? task.id === taskIdFromMeta : task.questionnaireId === localQuestionnaireId
  );
  const taskId = currentTask?.id;
  const hasFile = currentTask?.hasFile ?? false;

  // Determine if user has DPO access (Admin or Dpo role)
  const hasDpoAccess = user?.role === UserRole.Admin || user?.role === UserRole.Dpo;

  // For non-DPO users with uploaded files, show document2 (uploaded file) by default
  // For DPO users (Admin or Dpo) or when no file exists, show document1
  const initialTab = !hasDpoAccess && hasFile ? 'document2' : 'document1';

  // Initialize activeTab
  const [activeTab, setActiveTab] = useState<'document1' | 'document2'>(initialTab);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [approveTask, { loading: approveLoading }] = useDpoTaskSetApprovalMutation(companyId || '');
  const { enqueueSnackbar } = useSnackbar();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadFile, { loading: uploadLoading }] = useDpoTaskUploadFileMutation(companyId || '');
  const [deleteFile, { loading: deleteLoading }] = useDpoTaskDeleteFileMutation(companyId || '');

  // Query document preview images
  const {
    data: previewData,
    loading: previewLoading,
    error: previewError,
  } = useDpoTaskDocumentPreviewQuery(
    {
      taskId: taskId || '',
      companyId: companyId || '',
    },
    !hasFile // Skip query if no file exists
  );

  // Auto-switch to document2 tab when file is uploaded
  useEffect(() => {
    if (hasFile && activeTab === 'document1') {
      setActiveTab('document2');
    }
  }, [hasFile]);

  // Use the questionnaireId from props or from the task if available
  const document1QuestionnaireId = localQuestionnaireId || currentTask?.questionnaireId || '';

  // NOTE: second document is not my concern right now
  const document2QuestionnaireId = 'questionnaire-id-2'; // Replace with real ID as needed

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !taskId || !companyId) return;

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    if (!allowedTypes.includes(file.type)) {
      enqueueSnackbar('Only PDF and DOCX files are allowed', { variant: 'error' });
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10000000) {
      enqueueSnackbar('File size must be less than 10MB', { variant: 'error' });
      return;
    }

    try {
      await uploadFile({
        variables: { file, size: file.size, companyId, taskId },
      });
      enqueueSnackbar('File uploaded successfully', { variant: 'success' });
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Error uploading file', { variant: 'error' });
    }
  };

  const handleDeleteFile = async () => {
    if (!taskId || !companyId) return;

    try {
      await deleteFile({
        variables: { companyId, taskId },
      });
      enqueueSnackbar('File deleted successfully', { variant: 'success' });
      setShowDeleteConfirm(false);
      // Switch back to document1 tab after deleting
      setActiveTab('document1');
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Error deleting file', { variant: 'error' });
    }
  };

  const handleApprove = async (taskId: string) => {
    if (!companyId) return;
    try {
      await approveTask({
        variables: {
          companyId,
          taskId,
          approved: true,
        },
        optimisticResponse: {
          setDpoTaskApproval: true,
        },
        update: (cache) => {
          cache.modify({
            id: cache.identify({ __typename: 'DPOTask', id: taskId }),
            fields: {
              isApproved: () => true,
            },
          });
        },
      });
    } catch (err) {
      console.error('Error approving task:', err);
    }
  };
  const handleUnapprove = async (taskId: string) => {
    if (!companyId) return;
    try {
      await approveTask({
        variables: {
          companyId,
          taskId,
          approved: false,
        },
        optimisticResponse: {
          setDpoTaskApproval: false,
        },
        update: (cache) => {
          cache.modify({
            id: cache.identify({ __typename: 'DPOTask', id: taskId }),
            fields: {
              isApproved: () => false,
            },
          });
        },
      });
    } catch (err) {
      console.error('Error unapproving task:', err);
    }
  };

  return (
    <>
      <Modal open={true} onClose={onClose} sx={{ zIndex: 30 }} disableAutoFocus>
        <div className="fixed inset-0 flex items-center justify-center pointer-events-none">
          <Paper
            className="w-[75vw] h-[80vh] max-w-[1500px] flex flex-col pointer-events-auto rounded-none overflow-hidden p-[10px]"
            elevation={10}
            sx={{ borderRadius: '0px' }}
          >
            <CloseButton onClick={onClose} offset={false} btnColor="white" />

            {/* Content */}
            <div className="flex flex-1 overflow-hidden gap-2 p-0 bg-white">
              {/* Documents Section */}
              <div className="flex flex-2 flex-col gap-0 overflow-hidden">
                {/* Tabs - Only visible for admin users when uploaded document is available */}
                {hasDpoAccess && hasFile && (
                  <div className="flex gap-0 border-b-2 border-[#E3E3E3] w-full">
                    <button
                      onClick={() => setActiveTab('document1')}
                      className={cx(
                        'py-3 px-6 border-2 border-b-0 rounded-none cursor-pointer font-normal text-sm transition-all relative flex-1 -mr-[2px]',
                        activeTab === 'document1'
                          ? 'bg-white border-[#E3E3E3] border-b-2 border-b-white text-[#333] font-semibold z-1 -mb-[2px]'
                          : 'bg-[#F5F5F5] border-[#E3E3E3] text-[#666] hover:bg-[#FFFAED]'
                      )}
                    >
                      {t('route.dpo.document_comparer.original', 'Original')}
                    </button>
                    <button
                      onClick={() => setActiveTab('document2')}
                      className={cx(
                        'py-3 px-6 border-2 border-b-0 rounded-none cursor-pointer font-normal text-sm transition-all relative flex-1',
                        activeTab === 'document2'
                          ? 'bg-white border-[#E3E3E3] border-b-2 border-b-white text-[#333] font-semibold z-1 -mb-[2px]'
                          : 'bg-[#F5F5F5] border-[#E3E3E3] text-[#666] hover:bg-[#FFFAED]'
                      )}
                    >
                      {t('route.dpo.document_comparer.revision', 'Revision')}
                    </button>
                  </div>
                )}

                {/* Document Viewer */}
                {hasDpoAccess && activeTab === 'document1' ? (
                  <DpoDocumentViewer
                    localQuestionnaireId={document1QuestionnaireId}
                    documentNumber={documentNumber}
                    placeholder={t('route.dpo.document_comparer.no_document', 'Select a document to view')}
                  />
                ) : hasDpoAccess && activeTab === 'document2' && hasFile ? (
                  // Display uploaded document preview for document2
                  <div className="flex-1 overflow-hidden flex flex-col">
                    <UploadedDocumentHeader />
                    {taskId && companyId && (
                      <UploadedDocumentDownloadBar
                        taskId={taskId}
                        companyId={companyId}
                        hasFile={hasFile}
                        documentName={currentTask?.name}
                      />
                    )}
                    <div className="flex-1 overflow-auto bg-[#F9F9F9] rounded-none border-2 border-t-0 border-[#E3E3E3] flex flex-col">
                      <div className="p-5 flex-1 overflow-auto bg-white m-1 rounded-none border-[#E3E3E3]">
                        {previewLoading ? (
                          <DocumentSkeleton />
                        ) : previewError ? (
                          <div className="text-red-500">Error loading document preview</div>
                        ) : (
                          <div>
                            {previewData?.getDpoTaskDocumentPreview.map((img, index) => (
                              <img key={index} src={img} alt={`Document page ${index + 1}`} className="w-full mb-2" />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : !hasDpoAccess && hasFile ? (
                  // For non-admin users with uploaded file, show document2 (uploaded file preview)
                  <div className="flex-1 overflow-hidden flex flex-col">
                    <UploadedDocumentHeader />
                    {taskId && companyId && (
                      <UploadedDocumentDownloadBar
                        taskId={taskId}
                        companyId={companyId}
                        hasFile={hasFile}
                        documentName={currentTask?.name}
                      />
                    )}
                    <div className="flex-1 overflow-auto bg-[#F9F9F9] rounded-none border-2 border-t-0 border-[#E3E3E3] flex flex-col">
                      <div className="p-5 flex-1 overflow-auto bg-white m-1 rounded-none border-[#E3E3E3]">
                        {previewLoading ? (
                          <DocumentSkeleton />
                        ) : previewError ? (
                          <div className="text-red-500">Error loading document preview</div>
                        ) : (
                          <div>
                            {previewData?.getDpoTaskDocumentPreview.map((img, index) => (
                              <img key={index} src={img} alt={`Document page ${index + 1}`} className="w-full mb-2" />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : !hasDpoAccess ? (
                  // For non-admin users without file, show document1
                  <DpoDocumentViewer
                    localQuestionnaireId={document1QuestionnaireId}
                    documentNumber={documentNumber}
                    placeholder={t('route.dpo.document_comparer.no_document', 'Select a document to view')}
                  />
                ) : null}
              </div>

              {/* Right Sidebar with Chat and Actions */}
              <div className="flex flex-col flex-1 min-w-[250px] gap-2">
                {/* Chat Component */}
                {taskId && <DpoChat taskId={taskId} companyId={companyId || ''} />}

                {/* Action Buttons - Only visible for admin users */}
                {hasDpoAccess && (
                  <div className="px-4 pb-4 flex flex-col gap-3 bg-white border-2 border-[#E3E3E3] pt-4">
                    {/* Approve/Unapprove Task Button */}
                    <button
                      className={cx(
                        'w-full py-2 px-4 rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed text-[#333]',
                        currentTask?.isApproved
                          ? 'bg-[#E3E3E3] hover:bg-[#D0D0D0]'
                          : 'bg-secondary-500 hover:bg-secondary-600'
                      )}
                      onClick={() => {
                        if (!taskId) return;
                        if (currentTask?.isApproved) {
                          handleUnapprove(taskId);
                        } else {
                          handleApprove(taskId);
                        }
                      }}
                      disabled={
                        approveLoading ||
                        !taskId ||
                        !currentTask ||
                        (!currentTask.isApproved && currentTask.dpoState !== DpoState.COMPLETE)
                      }
                    >
                      {currentTask?.isApproved
                        ? t('route.dpo.document_comparer.withdraw_approval', 'Abnahme zurückziehen')
                        : t('route.dpo.document_comparer.approve_task', 'Aufgabe Abnehmen')}
                    </button>

                    {/* Hidden file input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                    />

                    {/* Conditional Upload or Delete Button */}
                    {hasFile ? (
                      <button
                        className="w-full bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={deleteLoading || !currentTask}
                      >
                        {deleteLoading ? 'Deleting...' : 'Delete Document'}
                      </button>
                    ) : (
                      <button
                        className="w-full bg-white border-2 border-[#E3E3E3] hover:bg-[#F9F9F9] text-[#333] py-2 px-4 rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadLoading || !currentTask}
                      >
                        {uploadLoading
                          ? t('route.dpo.document_comparer.uploading', 'Uploading...')
                          : t('route.dpo.document_comparer.upload_document', 'Upload Document')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Paper>
        </div>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} sx={{ zIndex: 31 }}>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this document? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteFile} color="error" disabled={deleteLoading}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
